import { t } from "@/languages/i18n"
import { PostData } from "@/lib/config/types"
import { getLocalizedHref } from "@/lib/locale-utils"
import { toImageSrc } from "@/lib/utils"
import { formatDate } from "@/utils/formatDate"
import Image from "next/image"
import Link from "next/link"

type RelatedPostsSectionProps = {
  posts: PostData[]
  isTamdaOne?: boolean
}

export default function RelatedPostsSection({ posts, isTamdaOne = false }: RelatedPostsSectionProps) {
  if (posts.length === 0) {
    return <p className="text-sm text-gray-500">{t("Không có bài viết liên quan")}</p>
  }

  // Layout grid cho tamdaone: desktop 3 cột, mobile 2 cột
  if (isTamdaOne) {
    return (
      <div className="flex flex-col sm:gap-4 gap-2.5">
        <h2 className="text-[1.2rem] leading-[29.76px] font-bold sm:font-bold sm:text-[1.8rem] sm:leading-[44.64px] text-[#333333] uppercase sm:pt-0 pt-5">
          {t("Bạn cũng có thể thích")}
        </h2>
        <div className="grid sm:grid-cols-3 grid-cols-2 gap-5">
          {posts.map(post => {
            const postTitle = post.attributes?.title || post.attributes?.name || ""
            const postSlug = post.attributes?.slug || ""
            const postLocale = post.attributes?.locale
            const postImage =
              post.attributes?.image?.data?.attributes?.url ||
              (post.attributes as any)?.cover?.data?.attributes?.url ||
              undefined
            const postPublishedAt = post.attributes?.publishedAt || post.attributes?.createdAt || ""
            const postCategory = post.attributes?.categories?.data?.[0]?.attributes?.name || ""

            return (
              <Link
                key={post.id}
                href={getLocalizedHref(postLocale, `/${postSlug}`)}
                className="group image-hover flex flex-col bg-white overflow-hidden"
              >
                {postImage && (
                  <div className="relative w-full sm:h-[254px] h-[150px] overflow-hidden rounded-lg">
                    <Image
                      src={toImageSrc(postImage)}
                      alt={postTitle}
                      fill
                      className="object-cover w-full h-full transition-all duration-300 group-hover:brightness-110 group-hover:scale-105"
                    />
                  </div>
                )}
                <div className="flex flex-col pt-3 sm:py-3 gap-2 transition-none">
                  {postCategory && (
                    <span className="text-xs text-[#1AAF4D] font-bold transition-none">{postCategory}</span>
                  )}
                  <h3 className="text-sm sm:text-base leading-[20px] sm:leading-[22px] font-semibold text-[#333] line-clamp-2 transition-none">
                    {postTitle}
                  </h3>
                  {postPublishedAt && (
                    <p className="text-base text-[#242F3E] font-medium opacity-70">
                      {formatDate(postPublishedAt).formattedDate}
                    </p>
                  )}
                </div>
              </Link>
            )
          })}
        </div>
      </div>
    )
  }

  // Layout list cho các site khác (giữ nguyên)
  return (
    <div className="flex flex-col sm:gap-4 gap-2.5">
      <h2 className="sm:text-lg sm:leading-[27.9px] font-base leading-[24.8px] font-bold sm:font-black text-[#333] uppercase sm:pt-0 pt-5">
        {t("Bạn cũng có thể thích")}
      </h2>
      <div className="flex flex-col">
        {posts.map((post, index) => {
          const postTitle = post.attributes?.title || post.attributes?.name || ""
          const postSlug = post.attributes?.slug || ""
          const postLocale = post.attributes?.locale
          const postDescription = post.attributes?.description || ""
          const postImage =
            post.attributes?.image?.data?.attributes?.url ||
            (post.attributes as any)?.cover?.data?.attributes?.url ||
            undefined
          const postPublishedAt = post.attributes?.publishedAt || post.attributes?.createdAt || ""

          return (
            <div key={post.id}>
              <Link href={getLocalizedHref(postLocale, `/${postSlug}`)} className="gap-2 group image-hover">
                <div className="flex sm:flex-row flex-col sm:gap-5 gap-2 bg-white overflow-hidden">
                  {postImage && (
                    <div className="relative w-full sm:h-[132px] h-[193px] overflow-hidden sm:w-[28.4%]">
                      <Image src={toImageSrc(postImage)} alt={postTitle} fill className="object-cover w-full h-full" />
                    </div>
                  )}
                  <div className="sm:w-[71.6%] w-full flex flex-col">
                    <h3 className="sm:text-xl sm:leading-[28px] text-base leading-[24.8px] font-semibold text-[#333] group-hover:text-[#1EAD46] transition-all duration-300">
                      {postTitle}
                    </h3>
                    <p className="sm:pt-2.5 sm:pb-1 pt-[6px] text-sm text-[#333] leading-[21.7px] font-normal line-clamp-3">
                      {postDescription}
                    </p>
                    {postPublishedAt && (
                      <p className="sm:text-xs text-[10px] sm:leading-[18.6px] leading-[15.5px] text-[#333] font-normal">
                        {formatDate(postPublishedAt).formattedDate}
                      </p>
                    )}
                  </div>
                </div>
              </Link>
              {index < posts.length - 1 && (
                <hr className="sm:my-[26px] my-4 border-0 border-t border-dashed border-[#ced4da]" />
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
